import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useUserStore } from '../store/userStore';
import { useAuthStore } from '../store/authStore';
import { UserRole } from '../types/models';
import { useForm, Controller } from 'react-hook-form';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { getRoleNames } from '../utils/roleUtils';
import { isAdmin, isSystemAdminUser } from '../utils/permissions';
import { projectApi } from '../api/projectApi';
import { ProjectUserRoleMapEntry } from '../types/project';

interface UserFormData {
  email: string;
  full_name: string;
  password: string;
  is_active: boolean;
  is_superuser: boolean;
}

interface UserEditData {
  email: string;
  full_name: string;
  password?: string;
  is_active: boolean;
  is_superuser?: boolean;
}

type UserOrderBy = 'full_name' | 'email' | 'status';

export const Users: React.FC = () => {
  const { users, isLoading, error, fetchUsers, createUser, updateUser, deleteUser } = useUserStore();
  const { user: currentUser } = useAuthStore(); // Get the current user from auth store
  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState<number | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [userRoleMappings, setUserRoleMappings] = React.useState<ProjectUserRoleMapEntry[]>([]);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  const { control, handleSubmit, reset, formState: { errors } } = useForm<UserFormData>();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [orderBy, setOrderBy] = React.useState<UserOrderBy>('full_name');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const cellBorder = {
    border: '1px solid #e8e8e8',
  };

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  React.useEffect(() => {
    const fetchUserRoleMappings = async () => {
      try {
        const roleMap = await projectApi.getProjectUserRoleMap();
        setUserRoleMappings(roleMap);
      } catch (err) {
        console.error('Failed to fetch project role assignments:', err);
        setUserRoleMappings([]);
      }
    };

    fetchUserRoleMappings();
  }, []);

  const userProjectRoles = React.useMemo(() => {
    const map = new Map<number, { projectId: number; projectName: string; projectCode: string; roles: string[] }[]>();

    userRoleMappings.forEach((entry) => {
      if (!map.has(entry.user_id)) {
        map.set(entry.user_id, []);
      }

      const projects = map.get(entry.user_id)!;
      let project = projects.find((p) => p.projectId === entry.project_id);

      if (!project) {
        project = {
          projectId: entry.project_id,
          projectName: entry.project_name,
          projectCode: entry.project_code,
          roles: [],
        };
        projects.push(project);
      }

      if (!project.roles.includes(entry.role)) {
        project.roles.push(entry.role);
      }
    });

    return map;
  }, [userRoleMappings]);

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const visibleUsers = React.useMemo(() => (
    users
      .filter(user => !isSystemAdminUser(user))
      .filter(user => {
        if (!normalizedSearch) return true;
        const fullName = user.full_name?.toLowerCase() || '';
        const email = user.email?.toLowerCase() || '';
        return fullName.includes(normalizedSearch) || email.includes(normalizedSearch);
      })
  ), [users, normalizedSearch]);

  const sortedUsers = React.useMemo(() => {
    const stabilized = visibleUsers.map((user, index) => ({ user, index }));

    stabilized.sort((a, b) => {
      let comparator = 0;
      if (orderBy === 'status') {
        comparator = Number(a.user.is_active) - Number(b.user.is_active);
      } else {
        const valueA = (a.user[orderBy] || '').toString().toLowerCase();
        const valueB = (b.user[orderBy] || '').toString().toLowerCase();
        comparator = valueA.localeCompare(valueB);
      }
      if (comparator === 0) {
        return a.index - b.index;
      }
      return order === 'asc' ? comparator : -comparator;
    });

    return stabilized.map(item => item.user);
  }, [visibleUsers, order, orderBy]);

  const paginatedUsers = React.useMemo(
    () => sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedUsers, page, rowsPerPage]
  );

  const renderUserEvents = (userId: number) => {
    const projects = userProjectRoles.get(userId) || [];

    if (!projects.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {projects.map((project) => (
          <Box key={`${userId}-${project.projectId}`} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {project.projectName} ({project.projectCode})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getRoleNames(project.roles as UserRole[])}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const handleCreateClick = () => {
    setSelectedUser(null);
    reset({
      email: '',
      full_name: '',
      password: '',
      is_active: true,
      is_superuser: false,
    });
    setFormDialogOpen(true);
  };

  const handleEditClick = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(userId);
      reset({
        email: user.email,
        full_name: user.full_name,
        is_active: user.is_active,
        is_superuser: user.is_superuser || false,
      });
      setFormDialogOpen(true);
    }
  };

  const handleDeleteClick = (userId: number) => {
    // Clear any previous error message when opening the delete dialog for a new user
    setDeleteError(null);
    setUserToDelete(userId);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: UserFormData) => {
    try {
      if (selectedUser) {
        // For update, omit password if it's empty
        const updateData: UserEditData = {
          email: data.email,
          full_name: data.full_name,
          is_active: data.is_active,
          is_superuser: data.is_superuser,
        };
        if (data.password) {
          updateData.password = data.password;
        }
        await updateUser(selectedUser, updateData);
        setSuccessMessage('User updated successfully');
      } else {
        // For create, password is required
        await createUser({
          email: data.email,
          full_name: data.full_name,
          password: data.password,
          is_active: data.is_active,
        });
        setSuccessMessage('User created successfully');
      }
      setFormDialogOpen(false);
    } catch (error) {
      console.error('Error saving user:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (userToDelete) {
      try {
        setDeleteError(null);
        await deleteUser(userToDelete);
        setDeleteDialogOpen(false);
      } catch (error: any) {
        console.error('Error deleting user:', error);
        
        // Extract the error message from the response
        // Try multiple ways to get the error detail
        let errorMessage = error.message || 
                          error.response?.data?.detail || 
                          (typeof error.response?.data === 'string' ? error.response.data : 'Failed to delete user');
        
        console.log('Original error message:', errorMessage);
        
        // Make the error message more user-friendly and informative
        if (errorMessage.includes('Cannot delete user:')) {
          // The backend already provides specific reasons, but let's enhance them
          if (errorMessage.includes('created projects')) {
            errorMessage = 'This user has created projects in the system. Please reassign or delete these projects before deleting this user.';
          } else if (errorMessage.includes('created issues')) {
            errorMessage = 'This user has created issues in the system. Please reassign or resolve these issues before deleting this user.';
          } else if (errorMessage.includes('assigned as PIC')) {
            errorMessage = 'This user is assigned as a PIC (Person In Charge) to one or more issues. Please reassign these issues before deleting this user.';
          } else if (errorMessage.includes('assigned as approver')) {
            errorMessage = 'This user is assigned as an approver to one or more issues. Please reassign these issues before deleting this user.';
          }
        }
        
        console.log('Enhanced error message:', errorMessage);
        setDeleteError(errorMessage);
      }
    }
  };

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: UserOrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  React.useEffect(() => {
    setPage(0);
  }, [searchQuery, rowsPerPage]);

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs 
        items={[
          { label: 'Events', path: '/projects' },
          { label: "User Settings" }
        ]} 
      />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5" component="h2">Users</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              {isAdmin(currentUser) && (
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  color="primary"
                  onClick={handleCreateClick}
                >
                  Add User
                </Button>
              )}
            </Box>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: '#e0e0e0' }}>
                <TableRow>
                  <TableCell sx={cellBorder} sortDirection={orderBy === 'full_name' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'full_name'}
                      direction={orderBy === 'full_name' ? order : 'asc'}
                      onClick={(event) => handleRequestSort(event, 'full_name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={cellBorder} sortDirection={orderBy === 'email' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'email'}
                      direction={orderBy === 'email' ? order : 'asc'}
                      onClick={(event) => handleRequestSort(event, 'email')}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={cellBorder}>Events</TableCell>
                  <TableCell sx={cellBorder} sortDirection={orderBy === 'status' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? order : 'asc'}
                      onClick={(event) => handleRequestSort(event, 'status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={cellBorder}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell sx={cellBorder}>{user.full_name}</TableCell>
                    <TableCell sx={cellBorder}>{user.email}</TableCell>
                    <TableCell sx={cellBorder}>{renderUserEvents(user.id)}</TableCell>
                    <TableCell sx={cellBorder}>
                      <Chip
                        label={user.is_active ? 'Active' : 'Inactive'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellBorder, width: '110px' }}>
                      {isAdmin(currentUser) && (
                        <>
                          <IconButton
                            color="primary"
                            onClick={() => handleEditClick(user.id)}
                          >
                            <EditIcon />
                          </IconButton>
                          {!isSystemAdminUser(user) && (
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteClick(user.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!paginatedUsers.length && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                      {isLoading ? 'Loading users...' : 'No users found'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={sortedUsers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </TableContainer>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Typography variant="body1">
            Are you sure you want to delete this user? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Form Dialog */}
      <Dialog open={formDialogOpen} onClose={() => setFormDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="full_name"
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Full Name"
                      fullWidth
                      error={!!errors.full_name}
                      helperText={errors.full_name?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>
              {!selectedUser && (
                <Grid item xs={12}>
                  <Controller
                    name="password"
                    control={control}
                    rules={{ required: !selectedUser ? 'Password is required for new users' : false }}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Password"
                        type="password"
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password?.message}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={value}
                          onChange={(e) => onChange(e.target.checked)}
                        />
                      }
                      label="Active"
                    />
                  )}
                />
              </Grid>
              {currentUser?.is_superuser && (
                <Grid item xs={12}>
                  <Controller
                    name="is_superuser"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={value}
                            onChange={(e) => onChange(e.target.checked)}
                          />
                        }
                        label="Super User (Hidden from user list, full access to all pages)"
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {selectedUser ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

    </Box>
  );
};
